// src/components/ProceduresInsightsDialog.tsx
import React, { FC, useMemo, useState } from "react";
import OpenInNewIcon from "@mui/icons-material/OpenInNew"; // you already have this

import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Stack,
  Typography,
  Chip,
  useTheme,
  alpha,
  Grid,
  ToggleButtonGroup,
  ToggleButton,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import dayjs from "dayjs";
import ReactECharts from "echarts-for-react";
import { SATOSLink } from "../Application/SATOSLink";
import { Translate } from "../Application/Translate";

type ProceduresInsightsDialogProps = {
  open: boolean;
  onClose: () => void;
  procedures: Array<{
    Id: number;
    Name?: string | null;
    Created?: string | null;
    Updated?: string | null;
    LastModified?: string | null;
    FolderId?: number | null;
  }>;
  archivedProjects: Array<{
    Id: number;
    Name: string;
    Created: string;
  }>;
  suggestions: Array<{
    Id: number;
    ProcedureId?: number;
    ProcedureName?: string;
    ChecklistId?: number;
    ChecklistName?: string;
    Description?: string;
    Status?: string;
    StepId?: number;
    Created: string;
    CreatedBy?: { Id: number; Name: string };
  }>;
  currentUserId?: number;
  // keep this in case you still want to trigger your own task flow
  onCreateTaskForProcedure?: (id: number, name?: string) => void;
};

const ProceduresInsightsDialog: FC<ProceduresInsightsDialogProps> = ({
  open,
  onClose,
  procedures,
  archivedProjects,
  suggestions,
  currentUserId,
  onCreateTaskForProcedure,
}) => {
  const theme = useTheme();
  const now = dayjs();

  const [lookbackDays, setLookbackDays] = useState<30 | 60 | 90>(60);
  const [staleAfterDays, setStaleAfterDays] = useState<90 | 180 | 365>(180);

  const lookbackCutoff = now.subtract(lookbackDays, "day");
  const staleCutoff = now.subtract(staleAfterDays, "day");

  // build suggestion deep link to /procedures/procedureUpdates/{tab}
  const buildSuggestionLink = (s: {
    ProcedureId?: number;
    ChecklistId?: number;
    Id?: number;
    StepId?: number;
    Status?: string;
    CreatedBy?: { Id: number; Name: string };
  }) => {
    const procedureId = s.ProcedureId;
    const checklistId = s.ChecklistId;
    if (!procedureId || !checklistId) return undefined;

    const status = (s.Status || "").toLowerCase();
    let inferredTab: "newImprov" | "myImprov" | "verified" | "declined";
    if (status === "verified") {
      inferredTab = "verified";
    } else if (status === "declined") {
      inferredTab = "declined";
    } else if (currentUserId && s.CreatedBy?.Id === currentUserId) {
      inferredTab = "myImprov";
    } else {
      inferredTab = "newImprov";
    }

    const qs = new URLSearchParams();
    qs.set("id", String(procedureId));
    qs.set("checklistId", String(checklistId));
    if (s.Id) qs.set("suggestionId", String(s.Id));
    if (s.StepId) qs.set("stepId", String(s.StepId));

    return `/procedures/procedureUpdates/${inferredTab}?${qs.toString()}`;
  };

  // stale procedures
  const staleProcedures = useMemo(() => {
    return procedures
      .map((p) => {
        const last =
          [p.Updated, p.LastModified, p.Created]
            .map((v) => (v ? dayjs(v) : null))
            .filter((d): d is dayjs.Dayjs => !!d && d.isValid())
            .sort((a, b) => b.valueOf() - a.valueOf())[0] || null;
        return {
          id: p.Id,
          name: (p.Name || "").trim() || `#${p.Id}`,
          last,
        };
      })
      .filter((p) => !p.last || p.last.isBefore(staleCutoff))
      .sort((a, b) => {
        const av = a.last ? a.last.valueOf() : 0;
        const bv = b.last ? b.last.valueOf() : 0;
        return av - bv;
      })
      .slice(0, 12);
  }, [procedures, staleCutoff]);

  // unresolved suggestions
  const unresolvedSuggestions = useMemo(() => {
    const activeStatuses = new Set(["Unverified", "Open", "Pending", ""]);
    return suggestions
      .filter(
        (s) =>
          activeStatuses.has((s.Status || "").trim()) &&
          dayjs(s.Created).isAfter(lookbackCutoff)
      )
      .sort((a, b) => dayjs(b.Created).valueOf() - dayjs(a.Created).valueOf())
      .slice(0, 12);
  }, [suggestions, lookbackCutoff]);

  // old unresolved
  const oldUnresolvedSuggestions = useMemo(() => {
    const activeStatuses = new Set(["Unverified", "Open", "Pending", ""]);
    const oldCutoff = now.subtract(30, "day");
    return suggestions
      .filter((s) => {
        const st = (s.Status || "").trim();
        if (!activeStatuses.has(st)) return false;
        const d = dayjs(s.Created);
        return d.isValid() && d.isBefore(oldCutoff);
      })
      .sort((a, b) => dayjs(a.Created).valueOf() - dayjs(b.Created).valueOf())
      .slice(0, 8);
  }, [suggestions, now]);

  // suggestion hotspots
  const suggestionHotspots = useMemo(() => {
    const counts = new Map<number, { name: string; count: number }>();
    for (const s of suggestions) {
      const id = s.ProcedureId;
      if (!id) continue;
      const name = (s.ProcedureName || "").trim() || `#${id}`;
      const current = counts.get(id) || { name, count: 0 };
      current.count += 1;
      counts.set(id, current);
    }
    return Array.from(counts.entries())
      .map(([id, v]) => ({ id, ...v }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 6);
  }, [suggestions]);

  // archived by week
  const archivedByWeek = useMemo(() => {
    const buckets: { key: string; start: dayjs.Dayjs }[] = [];
    let cursor = now.startOf("isoWeek").subtract(7, "week");
    for (let i = 0; i < 8; i++) {
      buckets.push({
        key: `${cursor.isoWeekYear()}-W${String(cursor.isoWeek()).padStart(2, "0")}`,
        start: cursor,
      });
      cursor = cursor.add(1, "week");
    }

    return buckets.map((b) => {
      const end = b.start.endOf("isoWeek");
      const count = archivedProjects.filter((ap) => {
        const d = dayjs(ap.Created);
        return (d.isSame(b.start) || d.isAfter(b.start)) && d.isBefore(end.add(1, "ms"));
      }).length;
      return { bucket: b.key, count };
    });
  }, [archivedProjects, now]);

  const archivedSpike = useMemo(() => {
    if (!archivedByWeek.length) return null;
    const values = archivedByWeek.map((x) => x.count);
    const mean = values.reduce((a, b) => a + b, 0) / values.length;
    const variance =
      values.reduce((a, b) => a + Math.pow(b - mean, 2), 0) / values.length || 0;
    const std = Math.sqrt(variance);
    const recent = archivedByWeek[archivedByWeek.length - 1];
    const isSpike = recent.count > mean + 2 * std && recent.count > 0;
    return isSpike ? { ...recent, mean: Math.round(mean), std: Math.round(std) } : null;
  }, [archivedByWeek]);

  // used recently but old
  const usageButNotUpdated = useMemo(() => {
    const recentArchivedNames = new Set(
      archivedProjects
        .filter((ap) => dayjs(ap.Created).isAfter(lookbackCutoff))
        .map((ap) => (ap.Name || "").trim())
        .filter(Boolean)
    );

    const nameToLast = new Map<
      string,
      { id: number; name: string; last: dayjs.Dayjs | null }
    >();
    for (const p of procedures) {
      const name = (p.Name || "").trim();
      if (!name) continue;
      const last =
        [p.Updated, p.LastModified, p.Created]
          .map((v) => (v ? dayjs(v) : null))
          .filter((d): d is dayjs.Dayjs => !!d && d.isValid())
          .sort((a, b) => b.valueOf() - a.valueOf())[0] || null;
      nameToLast.set(name, { id: p.Id, name, last });
    }

    const result: { id: number; name: string; last: dayjs.Dayjs | null }[] = [];
    for (const name of recentArchivedNames) {
      const proc = nameToLast.get(name);
      if (!proc) continue;
      if (!proc.last || proc.last.isBefore(staleCutoff)) {
        result.push(proc);
      }
    }

    return result
      .sort((a, b) => {
        const av = a.last ? a.last.valueOf() : 0;
        const bv = b.last ? b.last.valueOf() : 0;
        return av - bv;
      })
      .slice(0, 8);
  }, [procedures, archivedProjects, lookbackCutoff, staleCutoff]);

  // usage concentration
  const usageConcentration = useMemo(() => {
    if (!archivedProjects.length) {
      return { total: 0, top5Share: 0, top: [] as { name: string; count: number }[] };
    }
    const counts = new Map<string, number>();
    for (const ap of archivedProjects) {
      const name = (ap.Name || "").trim() || "(no name)";
      counts.set(name, (counts.get(name) ?? 0) + 1);
    }
    const arr = Array.from(counts.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);
    const total = archivedProjects.length;
    const top5 = arr.slice(0, 5);
    const top5Share = top5.reduce((a, b) => a + b.count, 0) / total;
    return { total, top5Share, top: top5 };
  }, [archivedProjects]);

  // top suggesters
  const topSuggesters = useMemo(() => {
    const counts = new Map<string, number>();
    for (const s of suggestions) {
      const name = s.CreatedBy?.Name?.trim();
      if (!name) continue;
      counts.set(name, (counts.get(name) ?? 0) + 1);
    }
    return Array.from(counts.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);
  }, [suggestions]);

  // maintenance queue
  const maintenanceQueue = useMemo(() => {
    const procById = new Map<number, { name: string; last: dayjs.Dayjs | null }>();
    for (const p of procedures) {
      const last =
        [p.Updated, p.LastModified, p.Created]
          .map((v) => (v ? dayjs(v) : null))
          .filter((d): d is dayjs.Dayjs => !!d && d.isValid())
          .sort((a, b) => b.valueOf() - a.valueOf())[0] || null;
      procById.set(p.Id, { name: (p.Name || "").trim() || `#${p.Id}`, last });
    }

    const suggestionCountByProc = new Map<number, number>();
    const unresolvedCountByProc = new Map<number, number>();
    for (const s of suggestions) {
      if (!s.ProcedureId) continue;
      suggestionCountByProc.set(
        s.ProcedureId,
        (suggestionCountByProc.get(s.ProcedureId) ?? 0) + 1
      );
      const st = (s.Status || "").trim();
      if (st === "" || st === "Unverified" || st === "Open" || st === "Pending") {
        unresolvedCountByProc.set(
          s.ProcedureId,
          (unresolvedCountByProc.get(s.ProcedureId) ?? 0) + 1
        );
      }
    }

    const recentUsageNames = new Set(
      archivedProjects
        .filter((ap) => dayjs(ap.Created).isAfter(lookbackCutoff))
        .map((ap) => (ap.Name || "").trim())
        .filter(Boolean)
    );

    const items: Array<{
      id: number;
      name: string;
      score: number;
      reasons: string[];
      last: dayjs.Dayjs | null;
    }> = [];

    for (const [id, { name, last }] of procById.entries()) {
      const reasons: string[] = [];
      let score = 0;

      if (!last || last.isBefore(staleCutoff)) {
        score += 30;
        reasons.push(
          `Stale: last update ${last ? last.format("YYYY-MM-DD") : "never"}`
        );
      }

      if (recentUsageNames.has(name)) {
        score += 30;
        reasons.push(`Used in last ${lookbackDays} days`);
      }

      const unresolved = unresolvedCountByProc.get(id) ?? 0;
      const totalSug = suggestionCountByProc.get(id) ?? 0;
      if (totalSug > 0) {
        score += Math.min(totalSug * 3, 15);
        reasons.push(`${totalSug} suggestions`);
      }
      if (unresolved > 0) {
        score += Math.min(unresolved * 4, 20);
        reasons.push(`${unresolved} unresolved`);
      }

      if (score > 0) {
        items.push({ id, name, score, reasons, last });
      }
    }

    return items.sort((a, b) => b.score - a.score).slice(0, 10);
  }, [
    procedures,
    suggestions,
    archivedProjects,
    lookbackCutoff,
    staleCutoff,
    lookbackDays,
  ]);

  const sparkOption = {
    backgroundColor: "transparent",
    grid: { left: 0, right: 0, top: 10, bottom: 0 },
    tooltip: { trigger: "axis" },
    xAxis: {
      type: "category",
      show: false,
      data: archivedByWeek.map((x) => x.bucket),
    },
    yAxis: { type: "value", show: false },
    series: [
      {
        type: "line",
        data: archivedByWeek.map((x) => x.count),
        symbol: "none",
        smooth: true,
        areaStyle: { opacity: 0.15 },
        lineStyle: { width: 2 },
      },
    ],
  };

  const severityChip = (score: number) => {
    if (score >= 60)
      return <Chip size="small" color="error" label="High" sx={{ ml: 1 }} />;
    if (score >= 35)
      return <Chip size="small" color="warning" label="Medium" sx={{ ml: 1 }} />;
    return <Chip size="small" label="Low" sx={{ ml: 1 }} />;
  };

  return (

    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        <Translate String="ProceduresInsightsDialog.Insights.Title">Content & Usage Insights</Translate>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: "absolute", right: 12, top: 10 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ bgcolor: theme.palette.background.default }}>
        {/* filters */}
        <Box
          sx={(theme) => ({
            mb: 2,
            p: 1.25,
            borderRadius: 2,
            bgcolor:
              theme.palette.mode === "light"
                ? "background.paper"
                : alpha(theme.palette.background.paper, 0.05),
            border: `1px solid ${alpha(theme.palette.divider, 0.4)}`,
          })}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            alignItems={{ xs: "flex-start", sm: "center" }}
            justifyContent="space-between"
            flexWrap="wrap"
          >
            {/* Look back */}
            <Stack direction="column" spacing={0.5}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, minWidth: 72, color: "text.secondary" }}
                >
                  <Translate String="ProceduresInsightsDialog.Filters.LookBack">Look back</Translate>
                </Typography>
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={lookbackDays}
                  onChange={(_, v) => v && setLookbackDays(v)}
                >
                  <ToggleButton value={30}>
                    <Translate String="ProceduresInsightsDialog.Filters.Toggle.30d">30d</Translate>
                  </ToggleButton>
                  <ToggleButton value={60}>
                    <Translate String="ProceduresInsightsDialog.Filters.Toggle.60d">60d</Translate>
                  </ToggleButton>
                  <ToggleButton value={90}>
                    <Translate String="ProceduresInsightsDialog.Filters.Toggle.90d">90d</Translate>
                  </ToggleButton>
                </ToggleButtonGroup>
              </Stack>

              <Typography
                variant="caption"
                sx={{ color: "text.secondary", maxWidth: 420 }}
              >
                <Translate String="ProceduresInsightsDialog.Filters.LookBackInfo">
                  Only include suggestions created within the last <strong>{lookbackDays} days</strong>.
                </Translate>
              </Typography>
            </Stack>

            {/* Stale after */}
            <Stack direction="column" spacing={0.5}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, minWidth: 80, color: "text.secondary" }}
                >
                  <Translate String="ProceduresInsightsDialog.Filters.StaleAfter">Stale after</Translate>
                </Typography>
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={staleAfterDays}
                  onChange={(_, v) => v && setStaleAfterDays(v)}
                >
                  <ToggleButton value={90}>
                    <Translate String="ProceduresInsightsDialog.Filters.Toggle.90d_2">90d</Translate>
                  </ToggleButton>
                  <ToggleButton value={180}>
                    <Translate String="ProceduresInsightsDialog.Filters.Toggle.180d">180d</Translate>
                  </ToggleButton>
                  <ToggleButton value={365}>
                    <Translate String="ProceduresInsightsDialog.Filters.Toggle.365d">365d</Translate>
                  </ToggleButton>
                </ToggleButtonGroup>
              </Stack>
              <Typography
                variant="caption"
                sx={{ color: "text.secondary", maxWidth: 420 }}
              >
                <Translate String="ProceduresInsightsDialog.Filters.StaleAfterInfo">
                  Mark procedures as "stale" if they haven’t been updated in this many days.
                  These are good candidates for a review or update.
                </Translate>
              </Typography>
            </Stack>
          </Stack>
        </Box>

        <Grid container spacing={2}>
          {/* LEFT column */}
          <Grid item xs={12} md={7}>
            {/* Maintenance queue */}
            <Box
              sx={{
                mb: 2,
                p: 2,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.primary.main, 0.04),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                <Translate String="ProceduresInsightsDialog.Maintenance.Title">Maintenance queue</Translate>
              </Typography>
              <Typography variant="body2" sx={{ mb: 1, color: "text.secondary" }}>
                <Translate String="ProceduresInsightsDialog.Maintenance.Description">
                  Highest impact items based on staleness, recent usage and suggestions.
                </Translate>
              </Typography>

              <Stack spacing={1}>
                {maintenanceQueue.map((item) => (
                  <Box
                    key={item.id}
                    sx={{
                      p: 1,
                      borderRadius: 1,
                      bgcolor: "background.paper",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 1,
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                        {item.name}
                        {severityChip(item.score)}
                      </Typography>
                      <Typography variant="caption" sx={{ display: "block" }}>
                        {item.reasons.join(" • ")}
                      </Typography>
                    </Box>

                    <Stack direction="row" spacing={1} alignItems="center">
                      <SATOSLink
                        target="_blank"
                        to="AccessProcedure"
                        params={[item.id]}
                        style={{ textDecoration: "none", color: "inherit" }}
                      >
                        <Button
                          variant="outlined"
                          color="primary"
                          size="small"
                          startIcon={<OpenInNewIcon fontSize="small" />}
                          onClick={(e: React.MouseEvent) => {
                            e.stopPropagation();
                          }}
                          sx={{
                            textTransform: "none",
                            fontWeight: 700,
                            fontSize: 12,
                            px: 1.25,
                            py: 0.5,
                            borderRadius: 1.5,
                          }}
                          aria-label={`${item.name}}`}
                        >
                          <Translate String="ProceduresInsightsDialog.Button.Open">Open</Translate>
                        </Button>
                      </SATOSLink>
                    </Stack>
                  </Box>
                ))}
                {!maintenanceQueue.length && (
                  <Typography variant="body2">
                    <Translate String="ProceduresInsightsDialog.Maintenance.Empty">Nothing to fix right now 🎉</Translate>
                  </Typography>
                )}
              </Stack>
            </Box>

            {/* Stale procedures */}
            <Box
              sx={{
                mb: 2,
                p: 2,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.warning.main, 0.05),
                border: `1px solid ${alpha(theme.palette.warning.main, 0.12)}`,
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                <Translate String="ProceduresInsightsDialog.Stale.Title">Procedures that might be stale</Translate>
              </Typography>
              <Typography variant="body2" sx={{ mb: 1.5, color: "text.secondary" }}>
                <Translate String="ProceduresInsightsDialog.Stale.Description">
                  Not updated in the last {staleAfterDays} days — good candidates to review.
                </Translate>
              </Typography>

              {staleProcedures.length ? (
                <Stack spacing={1}>
                  {staleProcedures.map((p) => (
                    <Stack
                      key={p.id}
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                        bgcolor: "background.paper",
                      }}
                    >
                      <Typography variant="body2" noWrap sx={{ maxWidth: "60%" }}>
                        {p.name}
                      </Typography>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Typography variant="caption" sx={{ color: "text.secondary" }}>
                          {p.last ? p.last.format("YYYY-MM-DD") : <Translate String="ProceduresInsightsDialog.Stale.Never">never</Translate>}
                        </Typography>

                        <SATOSLink
                          target="_blank"
                          to="AccessProcedure"
                          params={[p.id]}
                          style={{ textDecoration: "none", color: "inherit" }}
                        >
                          <Button
                            variant="outlined"
                            color="primary"
                            size="small"
                            startIcon={<OpenInNewIcon fontSize="small" />}
                            onClick={(e: React.MouseEvent) => {
                              e.stopPropagation();
                            }}
                            sx={{
                              textTransform: "none",
                              fontWeight: 700,
                              fontSize: 12,
                              px: 1.25,
                              py: 0.5,
                              borderRadius: 1.5,
                              ml: 1.25,
                            }}
                            aria-label={`Open ${p.name}`}
                          >
                            <Translate String="ProceduresInsightsDialog.Button.Open">Open</Translate>
                          </Button>
                        </SATOSLink>
                      </Stack>
                    </Stack>
                  ))}
                </Stack>
              ) : (
                <Typography variant="body2">
                  <Translate String="ProceduresInsightsDialog.Stale.Empty">Looks good — nothing stale 🎉</Translate>
                </Typography>
              )}
            </Box>

            {/* Suggestions waiting */}
            <Box
              sx={{
                mb: 2,
                p: 2,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.error.main, 0.03),
                border: `1px solid ${alpha(theme.palette.error.main, 0.08)}`,
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                <Translate String="ProceduresInsightsDialog.Suggestions.Title">Suggestions waiting for action</Translate>
              </Typography>
              <Typography variant="body2" sx={{ mb: 1.5, color: "text.secondary" }}>
                <Translate String="ProceduresInsightsDialog.Suggestions.Description">
                  Created but not Closed/Verified in the last {lookbackDays} days.
                </Translate>
              </Typography>

              <Stack spacing={1}>
                {unresolvedSuggestions.map((s) => {
                  const link = buildSuggestionLink({
                    ProcedureId: s.ProcedureId,
                    ChecklistId: s.ChecklistId,
                    Id: s.Id,
                    StepId: s.StepId,
                    Status: s.Status,
                    CreatedBy: s.CreatedBy,
                  });
                  return (
                    <Box
                      key={s.Id}
                      sx={{
                        p: 1,
                        borderRadius: 1,
                        bgcolor: "background.paper",
                      }}
                    >
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="flex-start"
                        gap={1}
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
                            {s.ProcedureName || <Translate String="ProceduresInsightsDialog.UnknownProcedure">Unknown procedure</Translate>}
                          </Typography>
                          <Typography variant="caption" sx={{ display: "block" }}>
                            {s.Description || s.ChecklistName || <Translate String="ProceduresInsightsDialog.NoDescription">No description</Translate>}
                          </Typography>
                          <Typography variant="caption" sx={{ color: "text.secondary" }}>
                            {dayjs(s.Created).fromNow()} • {s.Status || <Translate String="ProceduresInsightsDialog.Status.Unverified">Unverified</Translate>}
                          </Typography>
                        </Box>

                        {link && (
                          <a
                            target="_blank"
                            href={link}
                            style={{ textDecoration: "none", fontSize: 12, fontWeight: 500 }}
                          >
                            <Button
                              variant="outlined"
                              color="primary"
                              size="small"
                              startIcon={<OpenInNewIcon fontSize="small" />}
                              onClick={(e: React.MouseEvent) => {
                                e.stopPropagation();
                              }}
                              sx={{
                                textTransform: "none",
                                fontWeight: 700,
                                fontSize: 12,
                                px: 1.25,
                                py: 0.5,
                                borderRadius: 1.5,
                                ml: 1.25,
                              }}
                              aria-label={`Open suggestion for ${s.ProcedureName || "procedure"}`}
                            >
                              <Translate String="ProceduresInsightsDialog.Button.Open">Open</Translate>
                            </Button>
                          </a>
                        )}
                      </Stack>
                    </Box>
                  );
                })}
                {!unresolvedSuggestions.length && (
                  <Typography variant="body2">
                    <Translate String="ProceduresInsightsDialog.Suggestions.None">No pending suggestions 👌</Translate>
                  </Typography>
                )}
              </Stack>
            </Box>

            {/* Used recently but old */}
            <Box
              sx={{
                mb: 2,
                p: 2,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.info.main, 0.03),
                border: `1px solid ${alpha(theme.palette.info.main, 0.08)}`,
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                <Translate String="ProceduresInsightsDialog.UsedRecently.Title">Used recently but content is old</Translate>
              </Typography>
              <Typography variant="body2" sx={{ mb: 1.5, color: "text.secondary" }}>
                <Translate String="ProceduresInsightsDialog.UsedRecently.Description">
                  Ran in the last {lookbackDays} days, but older than {staleAfterDays} days.
                </Translate>
              </Typography>

              {usageButNotUpdated.length ? (
                <Stack spacing={1}>
                  {usageButNotUpdated.map((p) => (
                    <Stack
                      key={p.id}
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                        bgcolor: "background.paper",
                      }}
                    >
                      <Typography variant="body2" noWrap sx={{ maxWidth: "60%" }}>
                        {p.name}
                      </Typography>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Typography variant="caption" sx={{ color: "text.secondary" }}>
                          {p.last ? p.last.format("YYYY-MM-DD") : <Translate String="ProceduresInsightsDialog.UsedRecently.Never">never</Translate>}
                        </Typography>
                        <SATOSLink
                          target="_blank"
                          to="AccessProcedure"
                          params={[p.id]}
                          style={{ textDecoration: "none", color: "inherit" }}
                        >
                          <Button
                            variant="outlined"
                            color="primary"
                            size="small"
                            startIcon={<OpenInNewIcon fontSize="small" />}
                            onClick={(e: React.MouseEvent) => {
                              e.stopPropagation();
                            }}
                            sx={{
                              textTransform: "none",
                              fontWeight: 700,
                              fontSize: 12,
                              px: 1.25,
                              py: 0.5,
                              borderRadius: 1.5,
                              ml: 1.25,
                            }}
                            aria-label={`Open ${p.name}`}
                          >
                            <Translate String="ProceduresInsightsDialog.Button.Open">Open</Translate>
                          </Button>
                        </SATOSLink>
                      </Stack>
                    </Stack>
                  ))}
                </Stack>
              ) : (
                <Typography variant="body2">
                  <Translate String="ProceduresInsightsDialog.UsedRecently.Empty">Everything being used is reasonably fresh ✔︎</Translate>
                </Typography>
              )}
            </Box>
          </Grid>

          {/* RIGHT column */}
          <Grid item xs={12} md={5}>
            {/* Hotspots */}
            <Box
              sx={{
                mb: 2,
                p: 2,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.primary.main, 0.03),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                <Translate String="ProceduresInsightsDialog.Hotspots.Title">Procedures with the most suggestions</Translate>
              </Typography>
              <Typography variant="body2" sx={{ mb: 1.5, color: "text.secondary" }}>
                <Translate String="ProceduresInsightsDialog.Hotspots.Description">
                  High suggestion volume can mean high usage or unclear steps.
                </Translate>
              </Typography>

              <Stack spacing={1}>
                {suggestionHotspots.map((h, idx) => {
                  const procMatch = procedures.find(
                    (p) => (p.Name || "").trim() === (h.name || "").trim()
                  );
                  const procId = procMatch?.Id ?? 0;

                  return (
                    <Stack
                      key={h.id ?? idx}
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{
                        px: 1,
                        py: 0.6,
                        borderRadius: 1,
                        bgcolor: "background.paper",
                      }}
                    >
                      <Typography variant="body2" noWrap sx={{ maxWidth: "70%" }}>
                        {h.name}
                      </Typography>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Chip size="small" label={h.count} />
                        {procId !== 0 && (
                          <SATOSLink
                            target="_blank"
                            to="AccessProcedure"
                            params={[procId]}
                            style={{ textDecoration: "none", color: "inherit" }}
                          >
                            <Button
                              variant="outlined"
                              color="primary"
                              size="small"
                              startIcon={<OpenInNewIcon fontSize="small" />}
                              onClick={(e: React.MouseEvent) => {
                                e.stopPropagation();
                              }}
                              sx={{
                                textTransform: "none",
                                fontWeight: 700,
                                fontSize: 12,
                                px: 1.25,
                                py: 0.5,
                                borderRadius: 1.5,
                                ml: 1.25,
                              }}
                              aria-label={`Open ${h.name}`}
                            >
                              <Translate String="ProceduresInsightsDialog.Button.Open">Open</Translate>
                            </Button>
                          </SATOSLink>
                        )}
                      </Stack>
                    </Stack>
                  );
                })}
                {!suggestionHotspots.length && (
                  <Typography variant="body2">
                    <Translate String="ProceduresInsightsDialog.Hotspots.Empty">No hotspots detected.</Translate>
                  </Typography>
                )}
              </Stack>
            </Box>

            {/* concentration */}
            <Box
              sx={{
                mb: 2,
                p: 2,
                borderRadius: 2,
                bgcolor: "background.paper",
                border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
              }}
            >
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 1 }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  <Translate String="ProceduresInsightsDialog.Concentration.Title">Usage concentration</Translate>
                </Typography>
                <Chip
                  size="small"
                  label={`${Math.round(usageConcentration.top5Share * 100)}% in top 5`}
                  color={usageConcentration.top5Share > 0.6 ? "warning" : "default"}
                />
              </Stack>
              <Typography variant="body2" sx={{ mb: 1, color: "text.secondary" }}>
                {usageConcentration.total
                  ? <Translate String="ProceduresInsightsDialog.Concentration.Description">
                    Top 5 procedures account for {Math.round(usageConcentration.top5Share * 100)}% of {usageConcentration.total} archived runs.
                  </Translate>
                  : <Translate String="ProceduresInsightsDialog.Concentration.NoRecords">No usage records.</Translate>}
              </Typography>
              <Stack spacing={0.5}>
                {usageConcentration.top.map((t) => (
                  <Stack
                    key={t.name}
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography variant="body2" noWrap sx={{ maxWidth: "65%" }}>
                      {t.name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      {t.count}
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Box>

            {/* top suggesters */}
            <Box
              sx={{
                mb: 2,
                p: 2,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.success.main, 0.03),
                border: `1px solid ${alpha(theme.palette.success.main, 0.08)}`,
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                <Translate String="ProceduresInsightsDialog.TopSuggesters.Title">Top feedback contributors</Translate>
              </Typography>
              <Typography variant="body2" sx={{ mb: 1, color: "text.secondary" }}>
                <Translate String="ProceduresInsightsDialog.TopSuggesters.Description">People who create the most suggestions.</Translate>
              </Typography>
              {topSuggesters.length ? (
                <Stack spacing={0.75}>
                  {topSuggesters.map((u) => (
                    <Stack
                      key={u.name}
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Typography variant="body2">{u.name}</Typography>
                      <Chip size="small" label={u.count} />
                    </Stack>
                  ))}
                </Stack>
              ) : (
                <Typography variant="body2">
                  <Translate String="ProceduresInsightsDialog.TopSuggesters.Empty">No suggestion authors yet.</Translate>
                </Typography>
              )}
            </Box>

            {/* spike */}
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: "background.paper",
                border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  <Translate String="ProceduresInsightsDialog.Spike.Title">Recent archived trend</Translate>
                </Typography>
                {archivedSpike ? (
                  <Chip size="small" color="warning" label={<Translate String="ProceduresInsightsDialog.Spike.Label.Spike">Spike</Translate>} sx={{ fontWeight: 600 }} />
                ) : (
                  <Chip size="small" label={<Translate String="ProceduresInsightsDialog.Spike.Label.Normal">Normal</Translate>} />
                )}
              </Stack>
              <Typography variant="body2" sx={{ mb: 1, color: "text.secondary" }}>
                <Translate String="ProceduresInsightsDialog.Spike.TimeRange">Last 8 weeks</Translate>
              </Typography>
              <Box sx={{ height: 120 }}>
                <ReactECharts
                  style={{ height: "100%" }}
                  option={sparkOption}
                  notMerge
                  opts={{ renderer: "canvas" }}
                />
              </Box>
              {archivedSpike ? (
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  <Translate String="ProceduresInsightsDialog.Spike.Details">
                    Last week: {archivedSpike.count} (avg {archivedSpike.mean})
                  </Translate>
                </Typography>
              ) : (
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  <Translate String="ProceduresInsightsDialog.Spike.None">No unusual spike detected.</Translate>
                </Typography>
              )}
            </Box>

            {/* aging unresolved with suggestion deep-link */}
            {oldUnresolvedSuggestions.length > 0 && (
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.error.main, 0.025),
                  border: `1px solid ${alpha(theme.palette.error.main, 0.05)}`,
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  <Translate String="ProceduresInsightsDialog.Aging.Title">Aging unresolved suggestions</Translate>
                </Typography>
                <Typography variant="body2" sx={{ mb: 1, color: "text.secondary" }}>
                  <Translate String="ProceduresInsightsDialog.Aging.Description">Older than 30 days, still open.</Translate>
                </Typography>

                <Stack spacing={0.75}>
                  {oldUnresolvedSuggestions.map((s) => {
                    const link = buildSuggestionLink({
                      ProcedureId: s.ProcedureId,
                      ChecklistId: s.ChecklistId,
                      Id: s.Id,
                      StepId: s.StepId,
                      Status: s.Status,
                      CreatedBy: s.CreatedBy,
                    });
                    return (
                      <Box key={s.Id}>
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="flex-start"
                          gap={1}
                        >
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {s.ProcedureName || <Translate String="ProceduresInsightsDialog.UnknownProcedure">Unknown procedure</Translate>}
                            </Typography>
                            <Typography variant="caption" sx={{ display: "block" }}>
                              {s.Description || s.ChecklistName || <Translate String="ProceduresInsightsDialog.NoDescription">No description</Translate>}
                            </Typography>
                            <Typography variant="caption" sx={{ color: "text.secondary" }}>
                              <Translate String="ProceduresInsightsDialog.Aging.Opened">Opened {dayjs(s.Created).format("YYYY-MM-DD")} ({dayjs(s.Created).fromNow()})</Translate>
                            </Typography>
                          </Box>

                          {link && (
                            <a
                              target="_blank"
                              href={link}
                              style={{
                                textDecoration: "none",
                                fontSize: 12,
                                fontWeight: 500,
                              }}
                            >
                              <Button
                                variant="outlined"
                                color="primary"
                                size="small"
                                startIcon={<OpenInNewIcon fontSize="small" />}
                                onClick={(e: React.MouseEvent) => {
                                  e.stopPropagation();
                                }}
                                sx={{
                                  textTransform: "none",
                                  fontWeight: 700,
                                  fontSize: 12,
                                  px: 1.25,
                                  py: 0.5,
                                  borderRadius: 1.5,
                                  ml: 1.25,
                                }}
                              >
                                <Translate String="ProceduresInsightsDialog.Button.Open">Open</Translate>
                              </Button>
                            </a>
                          )}
                        </Stack>
                      </Box>
                    );
                  })}
                </Stack>
              </Box>
            )}
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default ProceduresInsightsDialog;
